import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { paymentWindow, type DueRule } from '@miraki/contracts';
import { add, money, toDb, ZERO, type Money as MoneyValue } from '@miraki/money';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Modal, Money, Note, PageHeader, Panel, Spinner, Table, Tabs, Tiles } from '../ui';
import { invalidateAll } from '../api/write-keys';
import { usePendingPayments } from '../api/hooks';
import {
  allocationCheck, dueRuleWords, groupSold, invoiceSelection, isCredit, magnitude, notYet,
  type CurrencyAmount, type Desk, type InvoiceRow, type SoldGroup, type SoldPiece,
} from './payables-view';
/* SL-04a — SL-04's bound, from the one place it is declared. The tab states it beside the deadline. */
import { saleDateBounds } from './shop/consignment-read';

/**
 * A-40 Sales & Payables desk — FN-04…FN-06, FN-08, FN-10, DB-09. The Accounts landing view.
 *
 * Three questions, one tab each: what has been SOLD and not yet invoiced; what has been INVOICED
 * and not yet paid, and how old it is; and what each retailer owes or is owed. Every figure is the
 * API's (GET /payables/desk), which composes the shared definitions — the payable is
 * `outstandingPayable()` per currency, the ageing is A-42's bands from the issue date — so this
 * desk and A-42's payables report cannot disagree. Nothing here is summed across currencies.
 *
 * The three actions — record an invoice, confirm a payment, reverse a sale — call
 * `ConsignmentService`, which the developers are building. Until they land, each answers NOT_YET
 * and the screen says so in place, naming what it waits for. The forms are real: their checks run
 * now, so the day the service lands nothing about this screen changes.
 *
 * Gated on `finance.view`; each action additionally on its own permission, and a person without it
 * is not offered the control (RL-05, "a control you cannot action is not shown").
 */

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A charge: a non-negative decimal, at most four places — NUMERIC(19,4), never a JS number. */
const CHARGE = /^\d{1,15}(\.\d{1,4})?$/;
/**
 * SL-04a adds a fourth. It is a tab of its own rather than a column on the By-retailer tab for one
 * reason: declaration is not a money state, and "not declared" sitting beside a payable invites
 * Accounts to read it as "owes nothing" — the exact opposite of what it means.
 */
type Tab = 'sold' | 'invoices' | 'retailers' | 'declarations';

/** Per currency, one line each. Below zero is a CREDIT (FN-08) and is said so — the same words the
 *  By-retailer tab and R-22 use, so a negative never reads as a figure owed. */
function Amounts({ list, empty }: { list: readonly CurrencyAmount[]; empty: string }) {
  const { t } = useTranslation();
  if (list.length === 0) return <span className="mk-dim">{empty}</span>;
  return (
    <>
      {list.map((a) => (
        <div key={a.currency}>
          {isCredit(a.amount)
            ? <>{t('payablesDesk.creditOf')} <Money amount={magnitude(a.amount)} currency={a.currency} /></>
            : <Money amount={a.amount} currency={a.currency} />}
        </div>
      ))}
    </>
  );
}

/**
 * The outcome of a write: done, not built yet (with what it waits for), or a real refusal.
 *
 * `ReactNode`, not `string`, for one reason: `waitingFor` names a SERVICE, and a service name is
 * not prose. It went through `t('payablesDesk.notYet', { what })` into a sentence that does not
 * declare `{{what}}`, so the identifier was simply dropped. R-20 and R-21 already had the answer —
 * the sentence, then the identifier in a dim parenthetical beside it, marked as the thing it is.
 */
type Outcome = { tone: 'them' | 'stop' | 'us'; text: ReactNode } | null;

/**
 * The complete variant code (NT-409115-YG-10K-H-VS2). Where a piece has no code, its colour and size
 * stand in, so the column is never blank for a piece that has a variant.
 */
function variantText(g: Pick<SoldGroup, 'variantCode' | 'goldColour' | 'ringSize'>, t: TFunction): string {
  if (g.variantCode) return g.variantCode;
  const parts = [g.goldColour, g.ringSize ? t('payablesDesk.form.ringSize', { size: g.ringSize }) : null];
  return parts.filter((x): x is string => Boolean(x)).join(' · ') || '—';
}

/** A small image beside the product's name. The name only: no code, no finish. */
function ProductCell({ g }: { g: Pick<SoldGroup, 'name' | 'imageUrl'> }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-3)' }}>
      {g.imageUrl
        ? <img src={g.imageUrl} alt="" width={40} height={40} style={{ objectFit: 'cover', borderRadius: 'var(--mk-radius-sm)', flex: 'none' }} />
        : null}
      <span>{g.name}</span>
    </div>
  );
}

export function PayablesDesk() {
  const { t } = useTranslation();
  const { can, portal } = useSession();
  const qc = useQueryClient();
  // CR-56 P4 — retailers' claims to have paid a purchase invoice, awaiting Admin. The route needs
  // `payment.verify`, so a user without it does not ask.
  const pending = usePendingPayments(can('payment.verify'));
  const claims = (pending.data ?? []).filter((p) => p.purpose === 'PURCHASE_INVOICE');
  /*
   * A-01's declaration tile drills to `/payables?tab=declarations`, so the query string chooses
   * the opening tab. Read once, for the initial value only: after that the tabs are the reader's.
   */
  const [search] = useSearchParams();
  const [tab, setTab] = useState<Tab>(search.get('tab') === 'declarations' ? 'declarations' : 'sold');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [invoicing, setInvoicing] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const [reversing, setReversing] = useState<SoldPiece | null>(null);

  const desk = useQuery({
    queryKey: ['payables', 'desk', portal?.code ?? null],
    queryFn: () => api.get<Desk>('/payables/desk'),
  });

  const sold = useMemo(() => desk.data?.awaitingInvoice ?? [], [desk.data]);
  const soldGroups = useMemo(() => groupSold(sold), [sold]);
  const selection = useMemo(() => invoiceSelection(sold.filter((p) => ticked.has(p.pieceId))), [sold, ticked]);

  /** Run a write; a NOT_YET is reported as such, anything else as the error it is. */
  const write = async (call: () => Promise<unknown>, done: string): Promise<boolean> => {
    setOutcome(null);
    try {
      await call();
      setOutcome({ tone: 'them', text: done });
      await invalidateAll(qc, [['payables']]);
      return true;
    } catch (e) {
      const waiting = notYet(e);
      setOutcome(waiting !== null
        ? {
          tone: 'us',
          text: (
            <>
              {t('payablesDesk.notYet')}
              {waiting.waitingFor ? <span className="mk-dim"> ({waiting.waitingFor})</span> : null}
            </>
          ),
        }
        : { tone: 'stop', text: e instanceof ApiError ? e.message : t('payablesDesk.failed') });
      return false;
    }
  };

  if (desk.isLoading) return <Spinner />;
  if (desk.error) return <Alert tone="stop">{desk.error.message}</Alert>;
  const d = desk.data!;
  /*
   * D75 — the record-invoice hint names the rule in force for the retailer the dialog is ABOUT,
   * not the portal's.
   *
   * It read `dueRuleWords(d.dueRule, 'phrase')`, which was right while no account could differ
   * from its portal and is wrong for exactly the accounts that now can — the ones Admin is most
   * likely to be invoicing. `retailerDueRules` carries every account's resolved rule; the
   * portal's stands in only for a retailer that is somehow not on the list.
   *
   * Still the PHRASE reading: the hint names the rule inside a sentence of its own, and the full
   * sentence read as two sentences run together.
   */
  const ruleFor = (retailerId: string) => d.retailerDueRules.find((x) => x.retailerId === retailerId)?.rule ?? d.dueRule;
  const rulePhraseFor = (retailerId: string) => {
    const words = dueRuleWords(ruleFor(retailerId), 'phrase');
    return t(words.key, words.values);
  };
  const none = t('payablesDesk.none');

  return (
    <>
      <PageHeader
        title={t('payablesDesk.title')}
        sub={t('payablesDesk.asOf', { portal: d.portal.name, asOf: d.asOf })}
      />
      {outcome ? <Alert tone={outcome.tone}>{outcome.text}</Alert> : null}

      <Tiles items={[
        { k: t('payablesDesk.outstanding'), v: <Amounts list={d.outstanding} empty={none} />, s: t('payablesDesk.outstandingHint') },
        { k: t('payablesDesk.overdue'), v: <Amounts list={d.overdue} empty={none} />, s: t('payablesDesk.overdueHint') },
        { k: t('payablesDesk.awaiting'), v: sold.length, s: t('payablesDesk.awaitingHint') },
        { k: t('payablesDesk.credits'), v: d.credits.length, s: t('payablesDesk.creditsHint') },
      ]} />

      <Tabs
        active={tab}
        onChange={(id) => setTab(id as Tab)}
        tabs={[
          { id: 'sold', label: t('payablesDesk.tabSold'), ...(sold.length ? { badge: String(sold.length) } : {}) },
          { id: 'invoices', label: t('payablesDesk.tabInvoices'), ...(d.openInvoices.length ? { badge: String(d.openInvoices.length) } : {}) },
          { id: 'retailers', label: t('payablesDesk.tabRetailers') },
          /*
           * SL-04a — absent where the portal does not bill a calendar month as one payable: there
           * is no month to close, so the deadline closes nothing. The badge counts accounts, not
           * money, and only once the month is worth chasing.
           */
          ...(d.declarations ? [{
            id: 'declarations', label: t('declarations.tab'),
            ...(d.declarations.actionable && d.declarations.count
              ? { badge: String(d.declarations.count) } : {}),
          }] : []),
        ]}
      />

      {tab === 'sold' ? (
        <Panel
          pad
          actions={can('sale_invoice.record') ? (
            <Button size="sm" disabled={!selection.ok} onClick={() => setInvoicing(true)}>
              {t('payablesDesk.recordInvoice')}
            </Button>
          ) : undefined}
        >
          <Note>{t('payablesDesk.soldNote')}</Note>
          {!selection.ok && selection.reason !== 'NONE'
            ? <Alert tone="stop">{t(`payablesDesk.selection.${selection.reason}`)}</Alert> : null}
          {sold.length === 0 ? (
            <Empty title={t('payablesDesk.soldEmpty')} body={t('payablesDesk.soldEmptyBody')} />
          ) : (
            <Table<SoldGroup>
              rows={soldGroups}
              rowKey={(g) => g.key}
              columns={[
                ...(can('sale_invoice.record') ? [{
                  header: '', center: true, width: '3%',
                  render: (g: SoldGroup) => {
                    const all = g.pieces.every((p) => ticked.has(p.pieceId));
                    return (
                      <input
                        type="checkbox" style={{ width: 'auto', margin: 0 }}
                        aria-label={t('payablesDesk.selectProduct', { name: g.name })}
                        checked={all}
                        onChange={(e) => setTicked((s) => {
                          const next = new Set(s);
                          for (const p of g.pieces) { if (e.target.checked) next.add(p.pieceId); else next.delete(p.pieceId); }
                          return next;
                        })}
                      />
                    );
                  },
                }] : []),
                { header: t('payablesDesk.colPiece'), render: (g) => <ProductCell g={g} /> },
                { header: t('payablesDesk.colVariant'), render: (g) => <code>{variantText(g, t)}</code> },
                { header: t('payablesDesk.colRetailer'), render: (g) => g.retailerName },
                { header: t('payablesDesk.colSoldOn'), render: (g) => g.soldOn ?? '—' },
                { header: t('payablesDesk.colSoldQty'), numeric: true, render: (g) => g.pieces.length },
                { header: t('payablesDesk.colStillHeld'), numeric: true, render: (g) => g.stillHeld ?? '—' },
                // One amount: the price of one item × the number sold (100 × 3 = 300 shows 300).
                { header: t('payablesDesk.colAmount'), numeric: true, render: (g) => <Money amount={g.total} currency={g.currency} /> },
                ...(can('sale.reverse') ? [{
                  header: '', numeric: true,
                  // Reverses ONE item of the group: the sale is reversed a piece at a time (SL-09).
                  render: (g: SoldGroup) => <Button size="sm" tone="ghost" onClick={() => setReversing(g.pieces[0]!)}>{t('payablesDesk.reverseOne')}</Button>,
                }] : []),
              ]}
            />
          )}
        </Panel>
      ) : null}

      {/* CR-56 P4 — claims are NOT payments: nothing here is in Outstanding until Admin verifies it on A-45. */}
      {tab === 'invoices' && claims.length > 0 ? (
        <Panel title={t('payablesDesk.claims.title', { count: claims.length })} pad>
          <Table
            rows={claims}
            rowKey={(c) => c.id}
            columns={[
              { header: t('payablesDesk.colRetailer'), render: (c) => c.retailerName },
              {
                header: t('payablesDesk.colReference'),
                render: (c) => (c.purchaseInvoiceId
                  ? <Link to={`/payables/invoices/${c.purchaseInvoiceId}`}><code>{c.purchaseInvoiceReference}</code></Link>
                  : <code>{c.purchaseInvoiceReference ?? '—'}</code>),
              },
              { header: t('payablesDesk.claims.colClaimed'), numeric: true, render: (c) => <Money amount={c.amount} currency={c.currency} /> },
              { header: t('payablesDesk.claims.colSubmitted'), render: (c) => c.submittedAt.slice(0, 10) },
              { header: t('payablesDesk.claims.colBank'), render: (c) => c.bankReference ?? '—' },
              {
                header: t('payablesDesk.claims.colProof'),
                render: (c) => (c.hasFile
                  ? <a href={`/api/payments/${c.id}/file`} target="_blank" rel="noreferrer">{t('payablesDesk.claims.viewProof')}</a>
                  : <span className="mk-dim">{t('payablesDesk.claims.noProof')}</span>),
              },
              {
                header: '',
                numeric: true,
                render: (c) => (c.purchaseInvoiceId
                  ? <Link to={`/payables/invoices/${c.purchaseInvoiceId}`}>{t('payablesDesk.claims.review')}</Link>
                  : null),
              },
            ]}
          />
        </Panel>
      ) : null}

      {tab === 'invoices' ? (
        <Panel pad>
          <Note>{t('payablesDesk.ageingNote')}</Note>
          <Table
            rows={d.ageing}
            rowKey={(b) => b.band}
            columns={[
              { header: t('payablesDesk.colBand'), render: (b) => t('payablesDesk.days', { band: b.band }) },
              { header: t('payablesDesk.colInvoices'), numeric: true, render: (b) => b.invoices },
              { header: t('payablesDesk.colOpen'), numeric: true, render: (b) => <Amounts list={b.open} empty="—" /> },
            ]}
          />
          <div style={{ marginTop: 'var(--mk-space-4)' }}>
            {d.openInvoices.length === 0 ? (
              <Empty title={t('payablesDesk.invoicesEmpty')} body={t('payablesDesk.invoicesEmptyBody')} />
            ) : (
              <Table<InvoiceRow>
                rows={d.openInvoices}
                rowKey={(i) => i.id}
                columns={[
                  // CR-56 — A-45, the pieces behind this invoice.
                  { header: t('payablesDesk.colReference'), render: (i) => <Link to={`/payables/invoices/${i.id}`}><code>{i.reference}</code></Link> },
                  { header: t('payablesDesk.colRetailer'), render: (i) => i.retailerName },
                  // Age is folded under the issue date: one column fewer, so the row (and its action button) fits the panel.
                  { header: t('payablesDesk.colIssued'), render: (i) => <>{i.issuedOn}<div className="mk-hint">{t('payablesDesk.ageDays', { count: i.age })} · {i.band}</div></> },
                  { header: t('payablesDesk.colDue'), render: (i) => (i.minDueOn && i.minDueOn !== i.dueOn ? <>{i.minDueOn} –<br />{i.dueOn}</> : i.dueOn) },
                  { header: t('payablesDesk.colAmount'), numeric: true, render: (i) => <Money amount={i.amount} currency={i.currency} /> },
                  { header: t('payablesDesk.colOpen'), numeric: true, render: (i) => <Money amount={i.open} currency={i.currency} /> },
                  {
                    header: t('payablesDesk.colState'),
                    render: (i) => (i.overdue
                      ? <Chip tone="stop">{t('payablesDesk.overdueChip')}</Chip>
                      : <Chip tone="us">{t(`payablesDesk.state.${i.state}`)}</Chip>),
                  },
                  ...(can('sale_payment.confirm') ? [{
                    header: '', numeric: true,
                    render: (i: InvoiceRow) => <Button size="sm" tone="ghost" onClick={() => setPaying(i.retailerId)}>{t('payablesDesk.confirmShort')}</Button>,
                  }] : []),
                ]}
              />
            )}
          </div>
        </Panel>
      ) : null}

      {/* Invoices that are paid in full or cancelled leave the open list above but are kept here. */}
      {tab === 'invoices' ? (
        <Panel title={t('payablesDesk.tabHistory') + ` (${d.invoiceHistory.length})`} pad>
          {d.invoiceHistory.length === 0 ? (
            <Empty title={t('payablesDesk.historyEmpty')} body={t('payablesDesk.historyEmptyBody')} />
          ) : (
            <Table<InvoiceRow>
              rows={d.invoiceHistory}
              rowKey={(i) => i.id}
              columns={[
                { header: t('payablesDesk.colReference'), render: (i) => <Link to={`/payables/invoices/${i.id}`}><code>{i.reference}</code></Link> },
                { header: t('payablesDesk.colRetailer'), render: (i) => i.retailerName },
                { header: t('payablesDesk.colIssued'), render: (i) => i.issuedOn },
                { header: t('payablesDesk.colDue'), render: (i) => i.dueOn },
                { header: t('payablesDesk.colAmount'), numeric: true, render: (i) => <Money amount={i.amount} currency={i.currency} /> },
                {
                  header: t('payablesDesk.colState'),
                  render: (i) => <Chip tone={i.state === 'CANCELLED' ? 'lapse' : 'done'}>{t(`payablesDesk.state.${i.state}`)}</Chip>,
                },
              ]}
            />
          )}
        </Panel>
      ) : null}

      {tab === 'retailers' ? (
        <Panel pad>
          <Note>{t('payablesDesk.retailersNote')}</Note>
          {d.byRetailer.length === 0 ? (
            <Empty title={t('payablesDesk.retailersEmpty')} body={t('payablesDesk.retailersEmptyBody')} />
          ) : (
            <Table
              rows={d.byRetailer}
              rowKey={(r) => r.retailerId}
              columns={[
                { header: t('payablesDesk.colRetailer'), render: (r) => r.retailerName },
                {
                  header: t('payablesDesk.colOutstanding'), numeric: true,
                  render: (r) => r.outstanding.map((o) => (
                    <div key={o.currency}>
                      {isCredit(o.amount)
                        ? <span>{t('payablesDesk.creditOf')} <Money amount={magnitude(o.amount)} currency={o.currency} /></span>
                        : <Money amount={o.amount} currency={o.currency} />}
                    </div>
                  )),
                },
              ]}
            />
          )}
        </Panel>
      ) : null}

      {/*
        * SL-04a — who has not declared this month.
        *
        * **Nothing on this tab refuses, blocks or charges anything** (I1): it is a list to act on
        * by picking up the telephone, which is why it carries no control at all. The copy says
        * what the list IS — accounts holding pieces with no sale reported for the period — because
        * the portal has no independent knowledge of a retailer's sales and cannot tell a quiet
        * month from a silent one. It is never labelled "non-compliant".
        *
        * And no amount of money appears, anywhere on it.
        */}
      {tab === 'declarations' && d.declarations ? (
        <Panel pad actions={<Chip tone={d.declarations.state === 'LATE' ? 'lapse' : 'them'}>
          {t(`declarations.state.${d.declarations.state}`)}
        </Chip>}>
          <Note>{t('declarations.period', { from: d.declarations.month.from, to: d.declarations.month.to })}</Note>
          <Note>{t('declarations.deadline', { deadline: d.declarations.deadline })}</Note>
          <Note>{t('declarations.conversation')}</Note>
          <Note>{t('declarations.backstop', { earliest: saleDateBounds(d.declarations.today).min })}</Note>
          {d.declarations.retailers.length === 0 ? (
            <Empty title={t('declarations.empty')} body={t('declarations.emptyBody')} />
          ) : (
            <Table
              rows={d.declarations.retailers}
              rowKey={(r) => r.retailerId}
              columns={[
                { header: t('declarations.colRetailer'),
                  render: (r) => <Link to={`/retailers/${r.retailerId}`}>{r.retailerName}</Link> },
                { header: t('declarations.colPieces'), numeric: true, render: (r) => r.piecesHeld },
                { header: t('declarations.colLastReported'),
                  render: (r) => <span className="mk-dim">{r.lastReportedOn ?? t('declarations.never')}</span> },
              ]}
            />
          )}
        </Panel>
      ) : null}

      {invoicing && selection.ok ? (
        <RecordInvoice
          selection={selection}
          pieces={sold.filter((p) => ticked.has(p.pieceId))}
          today={d.today}
          rule={ruleFor(selection.retailerId)}
          ruleText={rulePhraseFor(selection.retailerId)}
          onClose={() => setInvoicing(false)}
          onSubmit={async (body) => {
            const ok = await write(
              () => api.post(`/payables/retailers/${selection.retailerId}/invoices`, body),
              t('payablesDesk.invoiceRecorded', { reference: body.reference }),
            );
            setInvoicing(false);
            if (ok) setTicked(new Set());
          }}
        />
      ) : null}

      {paying ? (
        <ConfirmPayment
          invoices={d.openInvoices.filter((i) => i.retailerId === paying)}
          asOf={d.asOf}
          onClose={() => setPaying(null)}
          onSubmit={async (body) => {
            await write(() => api.post(`/payables/retailers/${paying}/payments`, body), t('payablesDesk.paymentConfirmed'));
            setPaying(null);
          }}
        />
      ) : null}

      {reversing ? (
        <ReverseSale
          piece={reversing}
          onClose={() => setReversing(null)}
          onSubmit={async (reason) => {
            await write(
              () => api.post(`/payables/pieces/${reversing.pieceId}/reverse-sale`, { reason }),
              t('payablesDesk.saleReversed', { job: reversing.productName ?? reversing.description }),
            );
            setReversing(null);
          }}
        />
      ) : null}
    </>
  );
}

// ------------------------------------------------------------------ FN-04 / FN-05

function RecordInvoice({ selection, pieces, today, rule, ruleText, onClose, onSubmit }: {
  selection: Extract<ReturnType<typeof invoiceSelection>, { ok: true }>;
  /** The ticked pieces, so each can be shown with its details and given its own charges. */
  pieces: readonly SoldPiece[];
  /** The PORTAL's day (D49), not the browser's — what the issue date defaults to. */
  today: string; ruleText: string;
  /** The rule in force for THIS account (D75: its own term, else the portal's). */
  rule: DueRule;
  onClose: () => void;
  onSubmit: (body: {
    reference: string; pieceIds: string[]; issuedOn: string; dueOn?: string;
    courierCharge?: string; miscCharges?: Array<{ label: string; amount: string }>;
  }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [reference, setReference] = useState('');
  /*
   * Courier and miscellaneous charges for the WHOLE invoice: two plain amount fields, blank for
   * none. Checked as the person types (`CHARGE`), added through `@miraki/money`, and checked again
   * by the server.
   */
  const [courier, setCourier] = useState('');
  /*
   * Miscellaneous is any charge or a tax, so each line has a NAME and an amount ("Handling" 100,
   * "Tax" 500). A line left entirely blank is ignored; a line with only half filled in is a mistake
   * worth saying so about.
   */
  const [misc, setMisc] = useState<Array<{ label: string; amount: string }>>([{ label: '', amount: '' }]);
  const chargeOk = (v: string) => v.trim() === '' || CHARGE.test(v.trim());
  const miscFilled = misc.filter((m) => m.label.trim() !== '' || m.amount.trim() !== '');
  const miscHalf = miscFilled.some((m) => m.label.trim() === '' || m.amount.trim() === '');
  const badCharge = !chargeOk(courier) || miscFilled.some((m) => !chargeOk(m.amount)) || miscHalf;
  const courierTotal: MoneyValue = CHARGE.test(courier.trim()) ? money(courier.trim()) : ZERO;
  const miscTotal: MoneyValue = miscFilled.reduce<MoneyValue>(
    (acc, m) => (CHARGE.test(m.amount.trim()) ? add(acc, money(m.amount.trim())) : acc), ZERO);
  const invoiceTotal = add(add(money(selection.subtotal), courierTotal), miscTotal);
  const [issuedOn, setIssuedOn] = useState(today);
  /*
   * CR-56 P2 — the due date is DEFAULTED, not decided. `dueTyped` is what Admin typed over the
   * default; until they type, the field shows the date the rule gives for the issue date as it
   * stands, and follows it when that changes. Admin is recording a document the accounting system
   * issued, so a typed date always wins and is sent as typed.
   *
   * **Only a typed date is sent.** An untouched default is left to the server, which derives the
   * same date from the same rule (pinned by `payment-window.spec.ts`). That keeps D75 where it
   * is: the server stays the one place that climbs the ladder, and a stale page cannot store a
   * date the rule would not have.
   */
  const [dueTyped, setDueTyped] = useState('');
  const win = paymentWindow(rule, issuedOn);
  const dueOn = dueTyped !== '' ? dueTyped : (win?.dueOn ?? '');
  const [busy, setBusy] = useState(false);
  const problem = reference.trim() === '' ? t('payablesDesk.form.referenceRequired')
    : reference.trim().length > 60 ? t('payablesDesk.form.referenceLong')
      : !DAY.test(issuedOn) ? t('payablesDesk.form.issuedRequired')
        : badCharge ? t('payablesDesk.form.chargeInvalid')
          : dueOn !== '' && dueOn < issuedOn ? t('payablesDesk.form.dueBeforeIssue')
            : null;
  return (
    <Modal
      wide
      title={t('payablesDesk.form.invoiceTitle', { retailer: selection.retailerName })}
      onClose={onClose}
      footer={(
        <>
          <Button tone="ghost" onClick={onClose}>{t('payablesDesk.form.cancel')}</Button>
          <Button
            disabled={problem !== null || busy}
            onClick={() => {
              setBusy(true);
              void onSubmit({
                reference: reference.trim(), pieceIds: selection.pieceIds, issuedOn,
                ...(dueTyped ? { dueOn: dueTyped } : {}),
                ...(courier.trim() !== '' ? { courierCharge: courier.trim() } : {}),
                ...(miscFilled.length > 0 ? { miscCharges: miscFilled.map((m) => ({ label: m.label.trim(), amount: m.amount.trim() })) } : {}),
              });
            }}
          >
            {t('payablesDesk.form.record')}
          </Button>
        </>
      )}
    >
      <p>{t('payablesDesk.form.covers', { count: selection.pieceIds.length })}</p>
      {/* What the invoice covers, as products: no Job Numbers, and no charges in this table. */}
      <Table<SoldGroup>
        rows={groupSold(pieces)}
        rowKey={(g) => g.key}
        columns={[
          { header: t('payablesDesk.colPiece'), render: (g) => <ProductCell g={g} /> },
          { header: t('payablesDesk.colVariant'), render: (g) => <code>{variantText(g, t)}</code> },
          { header: t('payablesDesk.colSoldQty'), numeric: true, render: (g) => g.pieces.length },
          { header: t('payablesDesk.colAmount'), numeric: true, render: (g) => <Money amount={g.total} currency={g.currency} /> },
        ]}
      />
      <div style={{ marginTop: 'var(--mk-space-5)' }}>
        <Field label={t('payablesDesk.form.courierCharge')}>
          <input inputMode="decimal" placeholder="0.00" value={courier} onChange={(e) => setCourier(e.target.value)} />
        </Field>
        {/* Named charges: Admin writes what the charge is and what it comes to. */}
        <Field label={t('payablesDesk.form.miscCharges')} hint={t('payablesDesk.form.miscHint')}>
          <div>
            {misc.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 'var(--mk-space-2)', marginBottom: 'var(--mk-space-2)' }}>
                <input
                  placeholder={t('payablesDesk.form.miscName')} maxLength={80} value={m.label}
                  aria-label={t('payablesDesk.form.miscNameN', { n: i + 1 })}
                  onChange={(e) => setMisc((l) => l.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <input
                  inputMode="decimal" placeholder={t('payablesDesk.form.miscAmount')} style={{ maxWidth: '9rem' }}
                  aria-label={t('payablesDesk.form.miscAmountN', { n: i + 1 })}
                  value={m.amount}
                  onChange={(e) => setMisc((l) => l.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                />
                {misc.length > 1
                  ? <Button size="sm" tone="ghost" onClick={() => setMisc((l) => l.filter((_, j) => j !== i))}>{t('payablesDesk.form.removeCharge')}</Button>
                  : null}
              </div>
            ))}
            <Button size="sm" tone="ghost" onClick={() => setMisc((l) => [...l, { label: '', amount: '' }])}>{t('payablesDesk.form.addCharge')}</Button>
          </div>
        </Field>
      </div>
      {/* Totals, from the same figures the server will add up (it checks them again). */}
      <p className="mk-hint">
        {t('payablesDesk.form.subtotal')} <Money amount={selection.subtotal} currency={selection.currency} />
        {' · '}{t('payablesDesk.form.courier')} <Money amount={toDb(courierTotal)} currency={selection.currency} />
        {' · '}{t('payablesDesk.form.misc')} <Money amount={toDb(miscTotal)} currency={selection.currency} />
        {' · '}<b>{t('payablesDesk.form.invoiceTotal')} <Money amount={toDb(invoiceTotal)} currency={selection.currency} /></b>
      </p>
      <Field label={t('payablesDesk.form.reference')} hint={t('payablesDesk.form.referenceHint')}>
        <input value={reference} maxLength={60} onChange={(e) => setReference(e.target.value)} />
      </Field>
      <Field label={t('payablesDesk.form.issuedOn')}>
        <input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
      </Field>
      {/*
        * D104 — the due window falls out of the rule and fills in by itself. MONTH_END has a range:
        * the earliest due date is the month end (read-only, it is the rule's), the latest is month
        * end plus the grace days (the stored due date, and what overdue is read against; Admin
        * may type over it). NET_DAYS has no grace, so it shows ONE date.
        */}
      {win && win.lastBeforeLate !== null ? (
        <>
          <Field label={t('payablesDesk.form.minDueOn')}>
            <input type="date" value={win.fallsDue} readOnly />
          </Field>
          <Field label={t('payablesDesk.form.maxDueOn')} hint={t('payablesDesk.form.dueHint', { rule: ruleText })}>
            <input type="date" value={dueOn} min={issuedOn} onChange={(e) => setDueTyped(e.target.value)} />
          </Field>
        </>
      ) : (
        <Field label={t('payablesDesk.form.dueOn')} hint={t('payablesDesk.form.dueHint', { rule: ruleText })}>
          <input type="date" value={dueOn} min={issuedOn} onChange={(e) => setDueTyped(e.target.value)} />
        </Field>
      )}
      {win ? (
        <div className="mk-hint">
          {win.lastBeforeLate === null
            ? t('payablesDesk.form.windowOne', { due: win.fallsDue })
            : t('payablesDesk.form.windowRange', { due: win.fallsDue, late: win.lastBeforeLate })}
        </div>
      ) : null}
      {problem ? <div className="mk-hint">{problem}</div> : null}
    </Modal>
  );
}

// ------------------------------------------------------------------ FN-06 / FN-08

function ConfirmPayment({ invoices, asOf, onClose, onSubmit }: {
  invoices: InvoiceRow[]; asOf: string;
  onClose: () => void;
  onSubmit: (body: { amount: string; receivedOn: string; bankReference?: string; allocations: Array<{ invoiceId: string; amount: string }> }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(asOf);
  const [bankReference, setBankReference] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /*
   * The first invoice, or nothing — and `?? ''` would have been the wrong answer now that the
   * title and the amount field both NAME what it carries. An empty fallback reads as "Confirm
   * payment — " and "Amount received ()", which is worse than the dialog not opening; the caller
   * only opens it from an invoice row, so there is always one.
   */
  const first = invoices[0];
  if (first === undefined) return null;
  const currency = first.currency;
  // One currency per payment: a payment is money received in one currency (AN-08).
  const same = invoices.filter((i) => i.currency === currency);
  const check = allocationCheck(amount, same.map((i) => ({ invoice: i, amount: alloc[i.id] ?? '' })));
  const dateOk = DAY.test(receivedOn) && receivedOn <= asOf;
  return (
    <Modal
      wide
      title={t('payablesDesk.form.paymentTitle', { retailer: first.retailerName })}
      onClose={onClose}
      footer={(
        <>
          <Button tone="ghost" onClick={onClose}>{t('payablesDesk.form.cancel')}</Button>
          <Button
            disabled={!check.ok || !dateOk || busy}
            onClick={() => {
              if (!check.ok) return;
              setBusy(true);
              void onSubmit({
                amount: amount.trim(), receivedOn,
                ...(bankReference.trim() ? { bankReference: bankReference.trim() } : {}),
                allocations: check.lines,
              });
            }}
          >
            {t('payablesDesk.form.confirm')}
          </Button>
        </>
      )}
    >
      <Field label={t('payablesDesk.form.amount', { currency })}>
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t('payablesDesk.form.receivedOn')} hint={dateOk ? undefined : t('payablesDesk.form.receivedFuture')}>
        <input type="date" value={receivedOn} max={asOf} onChange={(e) => setReceivedOn(e.target.value)} />
      </Field>
      <Field label={t('payablesDesk.form.bankReference')}>
        <input value={bankReference} maxLength={80} onChange={(e) => setBankReference(e.target.value)} />
      </Field>
      <Table<InvoiceRow>
        rows={same}
        rowKey={(i) => i.id}
        columns={[
          { header: t('payablesDesk.colReference'), render: (i) => <code>{i.reference}</code> },
          { header: t('payablesDesk.colIssued'), render: (i) => i.issuedOn },
          { header: t('payablesDesk.colOpen'), numeric: true, render: (i) => <Money amount={i.open} currency={i.currency} /> },
          {
            header: t('payablesDesk.form.allocate'), numeric: true,
            render: (i) => (
              <input
                inputMode="decimal" style={{ maxWidth: '9rem' }}
                aria-label={t('payablesDesk.form.allocateTo', { reference: i.reference })}
                value={alloc[i.id] ?? ''}
                onChange={(e) => setAlloc((a) => ({ ...a, [i.id]: e.target.value }))}
              />
            ),
          },
        ]}
      />
      {/*
        * The two figures go INSIDE their sentences, through `<Trans>`'s `<m/>` slot, rather than
        * being appended after a `t()` that asked for an `{{amount}}` nobody could pass. A figure
        * of money is a `<Money>` component (invariant 1 — a decimal string and its currency,
        * never a formatted number), so there was no string to interpolate and both brace pairs
        * printed verbatim: "Applied: {{amount}} USD 3,180 · {{amount}} will be left as a credit.
        * USD 0". The slot also leaves the word order to the sentence, which matters for the
        * credit line — the figure leads it.
        */}
      <p className="mk-hint">
        {check.ok
          ? (
            <>
              <Trans
                i18nKey="payablesDesk.form.allocated"
                components={{ m: <Money amount={check.allocated} currency={currency} /> }}
              />
              {' · '}
              <Trans
                i18nKey="payablesDesk.form.creditLeft"
                components={{ m: <Money amount={check.credit} currency={currency} /> }}
              />
            </>
          )
          : t(`payablesDesk.form.problem.${check.problem}`, { reference: 'reference' in check ? check.reference : '' })}
      </p>
    </Modal>
  );
}

// ------------------------------------------------------------------ SL-09

function ReverseSale({ piece, onClose, onSubmit }: { piece: SoldPiece; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 3 && reason.trim().length <= 500;
  return (
    <Modal
      title={t('payablesDesk.form.reverseTitle', { job: piece.productName ?? piece.description })}
      onClose={onClose}
      footer={(
        <>
          <Button tone="ghost" onClick={onClose}>{t('payablesDesk.form.cancel')}</Button>
          <Button tone="danger" disabled={!ok || busy} onClick={() => { setBusy(true); void onSubmit(reason.trim()); }}>
            {t('payablesDesk.form.reverse')}
          </Button>
        </>
      )}
    >
      <p>{t('payablesDesk.form.reverseBody', { retailer: piece.retailerName })}</p>
      <Field label={t('payablesDesk.form.reason')} hint={t('payablesDesk.form.reasonHint')}>
        <textarea rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}
